import { JSX } from 'react';
import { Col, Image, Row } from 'antd';
import ProductSwitcher, { ProductKeys } from './productSwitcher';
import { SupportDropdown } from './supportDropdown';
import { UserDropdown } from './userDropdown';
import { handleUserLogout } from '../../../../utils/index';
import { IOrganizationMember } from '../../../../store/models/redux/index';
import { useActiveMember } from '../../../../hooks/organizationMember/index';
import isEmpty from 'lodash/isEmpty';
import { Notifications } from './notificationDropdown';
import { useOrganizationLogo } from '@copilot/common/hooks/organization';
import { LinkedInStatusIndicator } from './linkedInStatusIndicator';
import { useActiveAccount } from '@copilot/common/hooks/account';

/**
 * [Smart Component] Smart container for the Prospecting Tool's top navigation bar
 */
export function ProspectingToolHeaderContainer(): JSX.Element {
    const activeMember = useActiveMember();
    const logoPath = useOrganizationLogo();
    const { data: currentAccount } = useActiveAccount();
    const isAccountDashboardEnabled = currentAccount?.isResellerAccount ?? false;

    const onLogout = () => void handleUserLogout();

    return (
        <ProspectingToolHeader
            member={activeMember}
            onLogout={onLogout}
            logoPath={logoPath}
            showProductSwitcher={isAccountDashboardEnabled}
        />
    );
}

type ProspectingToolHeaderProps = Readonly<{
    /**
     * The current active member
     */
    member: IOrganizationMember | null;
    /**
     * The path to the logo to display in the header
     */
    logoPath?: string;
    /**
     * Callback for logging out the current user
     */
    onLogout: () => void;
    /**
     * Whether to show the product switcher
     */
    showProductSwitcher: boolean;
}>;

function ProspectingToolHeader(props: ProspectingToolHeaderProps): JSX.Element {
    const { member, logoPath, onLogout, showProductSwitcher } = props;

    return (
        <Row justify="space-between">
            <Col>
                {!isEmpty(logoPath) && (
                    <Image
                        style={{ maxHeight: 40, width: 'auto' }}
                        src={logoPath}
                        preview={false}
                    />
                )}
                {showProductSwitcher && (
                    <ProductSwitcher currentProductKey={ProductKeys.teamsAccount} isDark />
                )}
            </Col>
            <Col style={{ display: 'flex' }}>
                <LinkedInStatusIndicator currentMember={member} />
                <SupportDropdown isDark />
                <Notifications isDark />
                <UserDropdown name={member?.name ?? ''} handleLogout={onLogout} isDark />
            </Col>
        </Row>
    );
}
